import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';
import { userQuotaAPI } from '../services/userQuotaAPI';
import { authService } from '../services/authService';
import { getQuotaStatus, getQuotaClasses } from '../config/appConfig';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [userQuota, setUserQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);

  // Helper para extraer mensajes de error de manera segura
  const getErrorMessage = (error, defaultMessage) => {
    // Si es un error de axios con respuesta del servidor
    if (error.response?.data) {
      const data = error.response.data;
      
      // Si el detail es un string
      if (typeof data.detail === 'string') {
        return data.detail;
      }
      
      // Si el detail es un array de errores de validación
      if (Array.isArray(data.detail)) {
        return data.detail.map(err => err.msg || err.message || 'Error de validación').join(', ');
      }
      
      // Si hay un campo message
      if (typeof data.message === 'string') {
        return data.message;
      }
    }
    
    // Si es un error simple con mensaje
    if (typeof error.message === 'string') {
      return error.message;
    }
    
    // Mensaje por defecto
    return defaultMessage;
  };

  // Datos de cuota reales o por defecto
  const quotaInfo = userQuota ? userQuotaAPI.formatQuotaInfo(userQuota) : {
    used: '0',
    total: '0',
    available: '0'
  };

  // Obtener estado y clases de la cuota usando configuración centralizada
  const quotaStatus = userQuota ? getQuotaStatus(userQuota.available_documents) : null;
  const quotaClasses = userQuota ? getQuotaClasses(userQuota.available_documents) : null;

  // Formulario para cambio de contraseña
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPasswordForm,
    watch
  } = useForm();

  // Formulario para actualizar perfil
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    setValue
  } = useForm({
    defaultValues: {
      full_name: user?.full_name || '',
      email: user?.email || ''
    }
  });

  // Establecer valores iniciales cuando se carga el usuario
  React.useEffect(() => {
    if (user) {
      setValue('full_name', user.full_name);
      setValue('email', user.email);
    }
  }, [user, setValue]);

  // Cargar cuota del usuario
  React.useEffect(() => {
    const loadUserQuota = async () => {
      if (!user) {
        setUserQuota(null);
        setQuotaLoading(false);
        return;
      }

      setQuotaLoading(true);
      try {
        const quotaResult = await userQuotaAPI.getUserQuota();
        if (quotaResult.success) {
          setUserQuota(quotaResult.data);
        } else {
          setUserQuota(null);
        }
      } catch (error) {
        setUserQuota(null);
      } finally {
        setQuotaLoading(false);
      }
    };

    loadUserQuota();
  }, [user]);

  const onChangePassword = async (data) => {
    setLoading(true);
    
    try {
      await authService.changePassword(data.current_password, data.new_password);
      toast.success('Contraseña cambiada exitosamente');
      resetPasswordForm();
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Error al cambiar la contraseña');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onUpdateProfile = async (data) => {
    setLoading(true);
    
    try {
      const updatedUser = await authService.updateProfile(data.full_name);

      // Actualizar el usuario en el contexto
      if (updateUser) {
        updateUser(updatedUser);
      }

      toast.success('Perfil actualizado exitosamente');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Error al actualizar el perfil');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const watchNewPassword = watch('new_password');

  return (
    <Layout>
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0">Mi Perfil</h1>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Información del Perfil */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              {/* Avatar del usuario */}
              <div 
                className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '80px', height: '80px', fontSize: '2rem', fontWeight: 'bold' }}
              >
                {user?.full_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              
              <h5 className="card-title mb-2">{user?.full_name}</h5>
              <p className="text-muted mb-3">{user?.email}</p>
              
              <div className="mb-3">
                <span className={`badge ${user?.is_active ? 'bg-success' : 'bg-danger'}`}>
                  {user?.is_active ? 'Cuenta Activa' : 'Cuenta Inactiva'}
                </span>
              </div>

              <div className="text-muted small">
                <div className="d-flex justify-content-between mb-1">
                  <span>Miembro desde:</span>
                  <span>{new Date().getFullYear()}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Último acceso:</span>
                  <span>Ahora</span>
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas de uso */}
          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-white">
              <h6 className="card-title mb-0">
                <i className="bi bi-graph-up me-2"></i>
                Estadísticas de uso
              </h6>
            </div>
            <div className="card-body">
              {quotaLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary"></div>
                  <p className="mt-2 text-muted small">Cargando estadísticas...</p>
                </div>
              ) : (
                <>
                  {/* Información de documentos */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Estado de documentos</span>
                      <span className={`badge ${quotaClasses ? quotaClasses.bg : 'bg-success'}`}>
                        {quotaInfo.available} restantes
                      </span>
                    </div>
                    
                    {/* Alerta visual basada en documentos restantes */}
                    <div className={`alert py-2 mb-2 ${quotaClasses ? quotaClasses.alert : 'alert-success'}`}>
                      <div className="d-flex align-items-center">
                        <i className={`bi ${quotaStatus ? quotaStatus.icon : 'bi-check-circle-fill'} me-2`}></i>
                        <div className="small">
                          <strong>
                            {quotaStatus ? quotaStatus.label : 'Cuota Saludable'}
                          </strong>
                          <div className="text-muted">
                            {quotaStatus ? quotaStatus.message : `Tienes ${quotaInfo.available} documentos disponibles`}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {userQuota && quotaStatus && quotaStatus.status !== 'healthy' && (
                      <div className={`small ${quotaClasses.text}`}>
                        <i className={`bi ${quotaStatus.icon} me-1`}></i>
                        {quotaStatus.status === 'critical' 
                          ? `¡Solo quedan ${quotaInfo.available} documentos!` 
                          : `Quedan ${quotaInfo.available} documentos`
                        }
                      </div>
                    )}
                  </div>

                  {/* Información adicional solo si hay datos */}
                  {userQuota && (
                    <>
                      {/* Botón de compra - más prominente si la cuota está baja */}
                      <div className="d-grid mb-3">
                        <a 
                          href="/comprar-documentos" 
                          className={`btn ${quotaClasses ? quotaClasses.btn : 'btn-success'}`}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          {quotaStatus && quotaStatus.status === 'critical'
                            ? '¡Comprar más documentos YA!' 
                            : 'Comprar más documentos'
                          }
                        </a>
                      </div>

                      {/* Información de cuota */}
                      <div className="pt-3 border-top">
                        <div className="row text-center">
                          <div className="col-4">
                            <div className="text-muted small">Usados</div>
                            <div className="fw-bold text-primary">{quotaInfo.used}</div>
                          </div>
                          <div className="col-4">
                            <div className="text-muted small">Restantes</div>
                            <div className={`fw-bold ${quotaClasses ? quotaClasses.text : 'text-success'}`}>
                              {quotaInfo.available}
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="text-muted small">Total</div>
                            <div className="fw-bold text-info">
                              {quotaInfo.total}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Mensaje cuando no hay datos de cuota */}
                  {!userQuota && !quotaLoading && (
                    <div className="text-center py-3">
                      <i className="bi bi-exclamation-circle text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                      <p className="text-muted small mb-0">No se pudieron cargar las estadísticas de cuota</p>
                      <button 
                        className="btn btn-outline-primary btn-sm mt-2"
                        onClick={() => window.location.reload()}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Reintentar
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Panel de Administración - Solo para administradores */}
          {/* REMOVIDO: El acceso de administración se realiza desde el menú lateral */}
        </div>

        {/* Formularios */}
        <div className="col-lg-8">
          {/* Actualizar Información Personal */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white">
              <h5 className="card-title mb-0">
                <i className="bi bi-person me-2"></i>
                Información Personal
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleProfileSubmit(onUpdateProfile)}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="full_name" className="form-label">
                      Nombre Completo <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${profileErrors.full_name ? 'is-invalid' : ''}`}
                      id="full_name"
                      {...registerProfile('full_name', {
                        required: 'El nombre completo es requerido',
                        minLength: {
                          value: 2,
                          message: 'El nombre debe tener al menos 2 caracteres'
                        }
                      })}
                    />
                    {profileErrors.full_name && (
                      <div className="invalid-feedback">
                        {profileErrors.full_name.message}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="email" className="form-label">
                      Correo Electrónico
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      {...registerProfile('email')}
                      disabled
                    />
                    <div className="form-text">
                      El correo electrónico no se puede cambiar.
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Actualizando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        Actualizar Información
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Cambiar Contraseña */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white">
              <h5 className="card-title mb-0">
                <i className="bi bi-shield-lock me-2"></i>
                Cambiar Contraseña
              </h5>
            </div>
            <div className="card-body">
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                Tu nueva contraseña debe tener al menos 8 caracteres.
              </div>

              <form onSubmit={handlePasswordSubmit(onChangePassword)}>
                <div className="row g-3">
                  <div className="col-12">
                    <label htmlFor="current_password" className="form-label">
                      Contraseña Actual <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.current_password ? 'is-invalid' : ''}`}
                      id="current_password"
                      {...registerPassword('current_password', {
                        required: 'La contraseña actual es requerida'
                      })}
                    />
                    {passwordErrors.current_password && (
                      <div className="invalid-feedback">
                        {passwordErrors.current_password.message}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="new_password" className="form-label">
                      Nueva Contraseña <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.new_password ? 'is-invalid' : ''}`}
                      id="new_password"
                      {...registerPassword('new_password', {
                        required: 'La nueva contraseña es requerida',
                        minLength: {
                          value: 8,
                          message: 'La contraseña debe tener al menos 8 caracteres'
                        }
                      })}
                    />
                    {passwordErrors.new_password && (
                      <div className="invalid-feedback">
                        {passwordErrors.new_password.message}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="confirm_password" className="form-label">
                      Confirmar Nueva Contraseña <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className={`form-control ${passwordErrors.confirm_password ? 'is-invalid' : ''}`}
                      id="confirm_password"
                      {...registerPassword('confirm_password', {
                        required: 'Confirma tu nueva contraseña',
                        validate: value => 
                          value === watchNewPassword || 'Las contraseñas no coinciden'
                      })}
                    />
                    {passwordErrors.confirm_password && (
                      <div className="invalid-feedback">
                        {passwordErrors.confirm_password.message}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Cambiando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-check me-2"></i>
                        Cambiar Contraseña
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary"
                    onClick={() => resetPasswordForm()}
                  >
                    Limpiar Formulario
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
